import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  CircularProgress,
} from "@mui/material";
import {
  School as SchoolIcon,
  Search as SearchIcon,
  Reply as ReplyIcon,
  Visibility as ViewIcon,
  Help as HelpIcon,
  AutoAwesome as SuggestIcon,
} from "@mui/icons-material";
import toast from "react-hot-toast";
import { useAuth } from "../contexts/AuthContext";
import PageContentLayout from "../components/layout/PageContentLayout";
import apiService from "../services/apiService";

const PAGE_SIZE = 30;

const SchoolQuery = () => {
  const { user } = useAuth();
  const [selectedTab, setSelectedTab] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [queries, setQueries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [replyOpen, setReplyOpen] = useState(false);
  const [selectedQuery, setSelectedQuery] = useState(null);
  const [replyMessage, setReplyMessage] = useState("");
  const [suggesting, setSuggesting] = useState(false);
  const [sending, setSending] = useState(false);

  const loadQueries = useCallback(async (offset = 0, append = false) => {
    if (append) setLoadingMore(true);
    else setLoading(true);
    const result = await apiService.getSchoolQueries(PAGE_SIZE, offset);
    if (!result.success) {
      const remark = String(result.error || result.message || "");
      if (!/no record/i.test(remark)) {
        toast.error(remark || "Failed to load queries");
      }
      if (!append) setQueries([]);
      setHasMore(false);
    } else {
      const rows = Array.isArray(result.data) ? result.data : [];
      setQueries((prev) => (append ? prev.concat(rows) : rows));
      setHasMore(rows.length === PAGE_SIZE);
    }
    setLoading(false);
    setLoadingMore(false);
  }, []);

  useEffect(() => {
    loadQueries(0, false);
  }, [loadQueries]);

  const filteredQueries = queries.filter((query) => {
    const replies = Array.isArray(query.reply) ? query.reply : [];
    const open = replies.length === 0;
    const matchesTab =
      selectedTab === 0 ||
      (selectedTab === 1 && open) ||
      (selectedTab === 2 && !open);
    const hay = [
      query.school_code,
      query.sender_name,
      query.query,
      query.query_type,
      query.query_to,
    ]
      .map((value) => String(value || "").toLowerCase())
      .join(" ");
    const q = searchTerm.trim().toLowerCase();
    return matchesTab && (!q || hay.includes(q));
  });

  const handleViewQuery = (query) => {
    setSelectedQuery(query);
    setDetailOpen(true);
  };

  const handleReply = (query) => {
    setSelectedQuery(query);
    setReplyMessage("");
    setReplyOpen(true);
  };

  const handleSuggest = async () => {
    if (!selectedQuery?.query_id || suggesting) return;
    setSuggesting(true);
    const result = await apiService.suggestQueryReply(selectedQuery.query_id);
    setSuggesting(false);
    if (!result.success) {
      toast.error(result.error || result.message || "Could not suggest a reply");
      return;
    }
    setReplyMessage(result.reply || "");
  };

  const handleSubmitReply = async () => {
    const text = replyMessage.trim();
    if (!text || !selectedQuery || sending) return;
    const senderId = user?.admin_id || user?.user_id || "";
    if (!senderId) {
      toast.error("Your admin id is missing. Log in again, then send the reply.");
      return;
    }
    setSending(true);
    const result = await apiService.replyToSchoolQuery({
      sender_id: senderId,
      query: text,
      query_to: selectedQuery.sender_id || "",
      query_type: selectedQuery.query_type || "Reply",
      parent_query_id: selectedQuery.query_id,
      school_code: selectedQuery.school_code || "",
    });
    setSending(false);
    if (!result.success) {
      toast.error(result.error || result.message || "Could not send reply");
      return;
    }
    toast.success("Reply sent");
    setReplyOpen(false);
    setReplyMessage("");
    setSelectedQuery(null);
    loadQueries(0, false);
  };

  return (
    <PageContentLayout
      title="School Queries"
      subtitle="Read school tickets and draft a reply before you send it"
      breadcrumbs={[
        { label: "School Queries", icon: <HelpIcon sx={{ color: "#ffffff", fontSize: 18 }} /> },
      ]}
      backTo="/dashboard"
    >
      <Card sx={{ mb: 3 }}>
        <Tabs
          value={selectedTab}
          onChange={(e, newValue) => setSelectedTab(newValue)}
          sx={{ borderBottom: 1, borderColor: "divider" }}
        >
          <Tab label="All" />
          <Tab label="Open" />
          <Tab label="Replied" />
        </Tabs>
      </Card>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <TextField
            fullWidth
            placeholder="Search school, sender, or query"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 1, color: "text.secondary" }} />,
            }}
          />
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>School</TableCell>
                  <TableCell>From</TableCell>
                  <TableCell>Query</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Added</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                      <CircularProgress size={28} />
                    </TableCell>
                  </TableRow>
                ) : filteredQueries.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                      <Typography variant="body2" sx={{ color: "text.secondary" }}>
                        No queries found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredQueries.map((query) => {
                    const replies = Array.isArray(query.reply) ? query.reply : [];
                    const open = replies.length === 0;
                    return (
                      <TableRow key={query.query_id} hover>
                        <TableCell>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Avatar sx={{ width: 32, height: 32, bgcolor: "primary.main" }}>
                              <SchoolIcon fontSize="small" />
                            </Avatar>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {query.school_code || "—"}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{query.sender_name || query.sender_id || "—"}</Typography>
                        </TableCell>
                        <TableCell sx={{ maxWidth: 320 }}>
                          <Typography variant="body2" noWrap title={query.query}>
                            {query.query}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip label={query.query_type || "Query"} size="small" variant="outlined" />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={open ? "Open" : `Replied (${replies.length})`}
                            size="small"
                            color={open ? "warning" : "success"}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" sx={{ color: "text.secondary" }}>
                            {query.date_added}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <IconButton size="small" onClick={() => handleViewQuery(query)} color="primary" aria-label="View query">
                            <ViewIcon />
                          </IconButton>
                          <IconButton size="small" onClick={() => handleReply(query)} color="success" aria-label="Reply">
                            <ReplyIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
          {hasMore && !loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
              <Button
                variant="outlined"
                disabled={loadingMore}
                onClick={() => loadQueries(queries.length, true)}
              >
                {loadingMore ? "Loading…" : "Load more"}
              </Button>
            </Box>
          ) : null}
        </CardContent>
      </Card>

      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Query details</DialogTitle>
        <DialogContent>
          {selectedQuery ? (
            <Box sx={{ mt: 1 }}>
              <Typography variant="subtitle2" color="text.secondary">School</Typography>
              <Typography variant="body1" sx={{ fontWeight: 600, mb: 1.5 }}>
                {selectedQuery.school_code}
              </Typography>
              <Typography variant="subtitle2" color="text.secondary">From</Typography>
              <Typography variant="body1" sx={{ mb: 1.5 }}>
                {selectedQuery.sender_name || selectedQuery.sender_id}
              </Typography>
              <Typography variant="subtitle2" color="text.secondary">Message</Typography>
              <Typography variant="body1" sx={{ whiteSpace: "pre-wrap", mb: 2 }}>
                {selectedQuery.query}
              </Typography>
              {(selectedQuery.reply || []).length > 0 ? (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                    Replies
                  </Typography>
                  {selectedQuery.reply.map((reply) => (
                    <Card key={reply.query_id || reply.date_added} sx={{ mb: 1 }}>
                      <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
                        <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>{reply.query}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {(reply.sender_name || "Support") + " · " + (reply.date_added || "")}
                        </Typography>
                      </CardContent>
                    </Card>
                  ))}
                </Box>
              ) : null}
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailOpen(false)}>Close</Button>
          {selectedQuery ? (
            <Button
              variant="contained"
              onClick={() => {
                setDetailOpen(false);
                handleReply(selectedQuery);
              }}
            >
              Reply
            </Button>
          ) : null}
        </DialogActions>
      </Dialog>

      <Dialog open={replyOpen} onClose={() => !sending && setReplyOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Reply to query</DialogTitle>
        <DialogContent>
          {selectedQuery ? (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 1 }}>
              {selectedQuery.school_code} · {selectedQuery.query}
            </Typography>
          ) : null}
          <TextField
            fullWidth
            multiline
            rows={6}
            label="Reply message"
            value={replyMessage}
            onChange={(e) => setReplyMessage(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            startIcon={suggesting ? <CircularProgress size={16} /> : <SuggestIcon />}
            onClick={handleSuggest}
            disabled={suggesting || sending}
          >
            Suggest reply
          </Button>
          <Box sx={{ flex: 1 }} />
          <Button onClick={() => { setReplyOpen(false); setReplyMessage(""); }} disabled={sending}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSubmitReply} disabled={sending || suggesting || !replyMessage.trim()}>
            {sending ? "Sending…" : "Send reply"}
          </Button>
        </DialogActions>
      </Dialog>
    </PageContentLayout>
  );
};

export default SchoolQuery;
